import React, { useState, useCallback, useEffect } from 'react';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { TableSortDirection } from '@amzn/meridian/table/table';
import Box from '@amzn/meridian/box';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import Icon from '@amzn/meridian/icon';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import {
  fetchRegions,
  fetchTasks,
  fetchMeasureUnits,
  setCurrentPage,
  setIsCreateModalOpen,
  setSearchField,
  setSelectedTask,
  setTotalPages,
  TableDataRow,
} from 'src/store/adminConfigPageSlice/taskConfigPageSlice/taskConfigPageSlice';
import { deduplicateArray } from 'src/utils/helpers';
import { filterSortAndPaginateTableData } from 'src/utils/tableUtils';

const TaskTable = () => {
  const dispatch = useAppDispatch();
  const [sortColumn, setSortColumn] = useState<keyof TableDataRow>('label');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('ascending');
  const onSort = useCallback(
    ({ sortColumn, sortDirection }: { sortColumn: keyof TableDataRow; sortDirection: TableSortDirection }) => {
      setSortDirection(sortDirection);
      setSortColumn(sortColumn);
    },
    [],
  );
  const { tableData, searchField, currentPage, perPage } = useAppSelector((state) => state.taskConfigPage);
  const { userLoading } = useAppSelector((state) => state.user);
  const [sortedTableData, setSortedTableData] = useState(tableData);

  useEffect(() => {
    if (userLoading === 'fulfilled') {
      dispatch(fetchMeasureUnits());
      dispatch(fetchTasks());
      dispatch(fetchRegions());
    }
    return () => {
      dispatch(setSearchField(''));
    };
  }, [dispatch, userLoading]);

  useEffect(() => {
    // filter, sorting, and pagination
    const { paginatedData, totalPages } = filterSortAndPaginateTableData(
      tableData,
      searchField,
      sortColumn,
      sortDirection,
      currentPage,
      ['id'],
      perPage,
    );
    setSortedTableData(paginatedData);
    dispatch(setTotalPages(totalPages));
  }, [sortColumn, sortDirection, tableData, searchField, currentPage, perPage, dispatch]);

  useEffect(() => {
    // go back to first page when filter/sort
    dispatch(setCurrentPage(1));
  }, [dispatch, searchField, sortColumn, sortDirection]);

  return (
    <Box overflowY="scroll" overflowX="scroll" type="outline" className="view-and-edit-tasks__table">
      <Table
        headerRows={1}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={onSort}
        showDividers={true}
        fixHeaderRows={true}
        rowComponents={[UncontrolledExpandableRow, TableRow]}
      >
        <TableRow>
          <TableCell width="20%" sortColumn="label">
            Task Name
          </TableCell>
          <TableCell sortColumn="regions">State/Region/Province</TableCell>
          <TableCell sortColumn="countries">Country</TableCell>
          <TableCell sortColumn="identifyBodyLocation">Identify Body Location?</TableCell>
          <TableCell sortColumn="isMeasuredBy">Is Measured By?</TableCell>
          <TableCell sortColumn="description">Description</TableCell>
          <TableCell>Edit</TableCell>
        </TableRow>
        {sortedTableData.map((row) => (
          <UncontrolledExpandableRow key={row.id}>
            <TableCell>{row.label}</TableCell>
            <TableCell>{row.regions.length === 1 ? `${row.regions[0]}` : `${row.regions.length} regions`}</TableCell>
            <TableCell>
              {deduplicateArray(row.countries).length == 1
                ? row.countries[0]
                : `${deduplicateArray(row.countries).length} countries`}
            </TableCell>
            {row.regions.length > 1 &&
              row.regions.map((_, index) => (
                <TableRow key={index}>
                  <TableCell></TableCell>
                  <TableCell>{row.regions[index]}</TableCell>
                  <TableCell>{row.countries[index]}</TableCell>
                </TableRow>
              ))}
            <TableCell>{row.identifyBodyLocation ? 'Yes' : 'No'}</TableCell>
            <TableCell>{row.isMeasuredBy}</TableCell>
            <TableCell>{row.description}</TableCell>
            <TableCell>
              <div
                className="view-and-edit-tasks__edit-icon"
                onClick={() => {
                  dispatch(setIsCreateModalOpen(true));
                  dispatch(setSelectedTask(row));
                }}
              >
                <Icon tokens={editTokens}></Icon>
              </div>
            </TableCell>
          </UncontrolledExpandableRow>
        ))}
      </Table>
    </Box>
  );
};

export default TaskTable;
